import React, {useState, useEffect, useRef} from "react";
import {
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Chip,
  Container,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningIcon,
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
  AttachMoney as AttachMoneyIcon,
  Done as DoneIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Article as ArticleIcon,
  Category as CategoryIcon,
  Home as HomeIcon,
  CalendarMonth as CalendarMonthIcon,
  Event as EventIcon,
  LocationOn as LocationOnIcon,
  Build as BuildIcon,
  MonetizationOn as MonetizationOnIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Shield as ShieldIcon,
} from "@mui/icons-material";
import {
  useServiceRequestQueries,
  useNotification,
  ServiceRequest,
  formatDate,
  AttachmentGallery,
  VendorProfile,
} from "shared-lib";
import AllBidsPdfExport from "../components/AllBidsPdfExport";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import {
  QASection
} from "../components";

// Add formatFieldName utility function at the top of the file

// Helper function to format status display
const StatusChip = ({status}: {status: string}) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "info";
      case "closed":
        return "success";
      case "removed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return <AccessTimeIcon fontSize="small" />;
      case "closed":
        return <CheckCircleIcon fontSize="small" />;
      case "removed":
        return <CancelIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  return (
    <Chip
      label={status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
      color={getStatusColor(status) as any}
      size="medium"
      icon={getStatusIcon(status)}
    />
  );
};

// Format field name to be more readable
const formatFieldName = (fieldName: string): string => {
  // Handle camelCase - insert space before capital letters
  let formatted = fieldName.replace(/([A-Z])/g, ' $1');
  
  // Handle snake_case - replace underscores with spaces
  formatted = formatted.replace(/_/g, ' ');
  
  // Trim and capitalize each word
  return formatted
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const ServicePhotoCard = ({imageUrl, title}: {imageUrl: string; title: string}) => {
  const [error, setError] = useState(false);

  // If we have no image URL or have an error, show placeholder
  if (!imageUrl || error) {
    return (
      <Card sx={{maxWidth: 345, mb: 2}}>
        <Box sx={{height: 194, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5"}}>
          <Typography variant="body2" color="text.secondary">
            No image available
          </Typography>
        </Box>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{maxWidth: 345, mb: 2}}>
      <CardMedia component="img" height="194" image={imageUrl} alt={title} onError={() => setError(true)} />
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Helper function to properly extract service details from the service request
const extractServiceDetails = (serviceRequest: ServiceRequest): any => {
  if (!serviceRequest) return {};

  // For new model with embedded service details
  if (serviceRequest.serviceName && serviceRequest.serviceDetails) {
    return {
      serviceName: serviceRequest.serviceName,
      ...serviceRequest.serviceDetails,
    };
  }

  return {};
};

const ServiceRequestDetail: React.FC = () => {
  const navigate = useNavigate();
  const {clientId, requestId} = useParams<{clientId: string; requestId: string}>();

  const {useServiceRequest, useBidsByRequest, useUpdateBidStatus, useCloseServiceRequest} = useServiceRequestQueries();
  const {showNotification} = useNotification();

  // For bid confirmation dialogs
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [bidToUpdate, setBidToUpdate] = useState<{bidId: string; status: "accepted" | "rejected"} | null>(null);
  const [closeWithoutWinner, setCloseWithoutWinner] = useState(false);
  
  // Store vendor profiles for bids
  const [vendorProfiles, setVendorProfiles] = useState<Record<string, VendorProfile>>({});

  // Fetch service request data
  const {data: serviceRequest, isLoading: requestLoading, error: requestError} = useServiceRequest(requestId || "");

  // Fetch bids for this request
  const {data: bids, isLoading: bidsLoading, error: bidsError} = useBidsByRequest(requestId || "");

  // Load vendor profiles when bids are loaded
  useEffect(() => {
    const fetchVendorProfiles = async () => {
      if (!bids || bids.length === 0) return;
      
      const db = getFirestore();
      const vendorIds = bids.map(bid => bid.vendorId);
      const uniqueVendorIds = [...new Set(vendorIds)];
      
      const profiles: Record<string, VendorProfile> = {};
      
      for (const vendorId of uniqueVendorIds) {
        const vendorRef = doc(db, 'vendors', vendorId);
        const vendorDoc = await getDoc(vendorRef);
        
        if (vendorDoc.exists()) {
          profiles[vendorId] = {
            id: vendorDoc.id,
            ...vendorDoc.data()
          } as VendorProfile;
        }
      }
      
      setVendorProfiles(profiles);
    };
    
    fetchVendorProfiles();
  }, [bids]);

  // Mutation for updating bid status
  const updateBidStatusMutation = useUpdateBidStatus();
  const updateServiceRequestMutation = useCloseServiceRequest();

  const isLoading = requestLoading || bidsLoading;
  const error = requestError || bidsError;

  const [showImageError, setShowImageError] = useState(false);

  const handleBack = () => {
    navigate(`/clients/${clientId}/requests`);
  };

  // Open confirmation dialog for accepting/rejecting a bid
  const handleBidAction = (bidId: string, status: "accepted" | "rejected") => {
    setBidToUpdate({bidId, status});
    setConfirmDialogOpen(true);
  };

  // Handle close request without selecting winner
  const handleCloseRequest = () => {
    setCloseWithoutWinner(true);
    setConfirmDialogOpen(true);
  };

  // Handle confirmation dialog confirm action
  const handleConfirmAction = async () => {
    if (bidToUpdate) {
      try {
        // If accepting a bid, first reject all other bids before accepting the winning bid
        if (bidToUpdate.status === "accepted" && bids && bids.length > 0) {
          // Get all other pending bids (excluding the one being accepted)
          const otherPendingBids = bids.filter(
            bid => bid.id !== bidToUpdate.bidId && bid.intent === "bid" && (bid.status === "pending" || !bid.status)
          );

          // Reject all other bids first
          for (const bid of otherPendingBids) {
            await updateBidStatusMutation.mutateAsync({
              bidId: bid.id,
              status: "rejected",
            });
          }

          // Now accept the winning bid
          await updateBidStatusMutation.mutateAsync(bidToUpdate);
        } else {
          // Just update the single bid status if not accepting
          await updateBidStatusMutation.mutateAsync(bidToUpdate);
        }

        // Then close the service request with a winner
        await updateServiceRequestMutation.mutateAsync({
          id: requestId as string,
          withWinner: true,
        });

        showNotification({
          message: `Bid selected as winner and request closed successfully`,
          severity: "success",
        });
        setConfirmDialogOpen(false);
        setBidToUpdate(null);
      } catch (error) {
        showNotification({
          message: `Failed to select winning bid. Please try again.`,
          severity: "error",
        });
      }
    } else if (closeWithoutWinner) {
      try {
        // Update request status to removed (closed without winner)
        await updateServiceRequestMutation.mutateAsync({
          id: requestId as string,
          withWinner: false,
        });
        showNotification({
          message: `Request closed without selecting a winner`,
          severity: "success",
        });
        setConfirmDialogOpen(false);
        setCloseWithoutWinner(false);
      } catch (error) {
        showNotification({
          message: `Failed to close request. Please try again.`,
          severity: "error",
        });
      }
    }
  };

  // Close the confirmation dialog
  const handleCancelAction = () => {
    setConfirmDialogOpen(false);
    setBidToUpdate(null);
  };

  // Show error notification if there's an error
  const errorShownRef = useRef(false);
  useEffect(() => {
    if (error && !errorShownRef.current) {
      errorShownRef.current = true;
      showNotification({
        message: "Error loading request details. Please try again.",
        severity: "error",
      });
    }
  }, [error, showNotification]);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{py: 4}}>
        <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh"}}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !serviceRequest) {
    return (
      <Container maxWidth="lg" sx={{py: 4}}>
        <Box sx={{mb: 4}}>
          <Typography variant="h4" sx={{mb: 2}}>
            Error Loading Request Details
          </Typography>
          <Typography variant="body1" sx={{mb: 3}}>
            There was an error loading the request details. Please try again later.
          </Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} variant="contained">
            Back to Requests
          </Button>
        </Box>
      </Container>
    );
  }

  // Get bids with intent=bid (exclude declined ones)
  const actualBids = bids?.filter(bid => bid.intent === "bid") || [];
  // Count declined bids
  const declinedCount = bids?.filter(bid => bid.intent === "ignore").length || 0;

  return (
    <Container maxWidth="lg" sx={{py: {xs: 3, sm: 4}}}>
      {/* Summary card */}
      <Paper
        elevation={2}
        sx={{
          p: {xs: 2, sm: 3},
          mb: 4,
          borderRadius: "12px",
          border: "1px solid rgba(0,0,0,0.05)",
        }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: {xs: "1rem", md: "1.1rem"},
                mb: 1,
              }}>
              <LocationOnIcon sx={{verticalAlign: "middle", mr: 1, fontSize: "1.2rem", color: "primary.main"}} />
              {serviceRequest.address}, {serviceRequest.city}, {serviceRequest.state} {serviceRequest.zipCode}
            </Typography>
            <Box sx={{display: "flex", flexDirection: "column", gap: 1, mb: 2}}>
              <Grid container spacing={2} sx={{mt: 1}}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{display: "flex", alignItems: "flex-start", mb: 1}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mr: 1, minWidth: "60px"}}>
                      <CategoryIcon sx={{verticalAlign: "middle", mr: 0.5, fontSize: "1rem", color: "primary.main"}} />
                      Type:
                    </Typography>
                    <Typography variant="body2">{serviceRequest.serviceType}</Typography>
                  </Box>

                  <Box sx={{display: "flex", alignItems: "flex-start", mb: 1}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mr: 1, minWidth: "60px"}}>
                      <HomeIcon sx={{verticalAlign: "middle", mr: 0.5, fontSize: "1rem", color: "primary.main"}} />
                      Service:
                    </Typography>
                    <Typography variant="body2">{formatFieldName(serviceRequest.serviceName)}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{display: "flex", alignItems: "flex-start", mb: 1}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mr: 1, minWidth: "70px"}}>
                      <CalendarMonthIcon
                        sx={{verticalAlign: "middle", mr: 0.5, fontSize: "1rem", color: "primary.main"}}
                      />
                      Timeline:
                    </Typography>
                    <Typography variant="body2">
                      {serviceRequest.projectTimeline}
                      {serviceRequest.preferredDate && ` (${formatDate(serviceRequest.preferredDate)})`}
                    </Typography>
                  </Box>

                  <Box sx={{display: "flex", alignItems: "flex-start"}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mr: 1, minWidth: "70px"}}>
                      <EventIcon sx={{verticalAlign: "middle", mr: 0.5, fontSize: "1rem", color: "primary.main"}} />
                      Created:
                    </Typography>
                    <Typography variant="body2">{formatDate(serviceRequest.createdAt)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: {xs: "flex-start", md: "flex-end"},
              mt: {xs: 2, md: 0},
            }}>
            <Box sx={{width: {xs: "100%", md: "auto"}, display: "flex", flexDirection: "column", gap: 2}}>
              <StatusChip status={serviceRequest.status || "Active"} />

              {/* Add Close Request button if request is active (regardless of bids) */}
              {serviceRequest.status === "active" && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCloseRequest}
                  startIcon={<CloseIcon />}
                  sx={{
                    mt: 1,
                    borderRadius: "8px",
                  }}>
                  Close Request (No Winner)
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Service Details section */}
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          mt: 2,
          mb: 2,
          fontWeight: 600,
          borderBottom: "2px solid",
          borderColor: "primary.main",
          pb: 1,
          display: "inline-block",
        }}>
        <BuildIcon sx={{verticalAlign: "middle", mr: 1, color: "primary.main"}} />
        Service Details
      </Typography>

      <Grid container spacing={3}>
        {serviceRequest ? (
          <Grid item xs={12}>
            <Paper
              elevation={1}
              sx={{
                p: {xs: 2, sm: 3},
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.05)",
                boxShadow: "0px 4px 20px -4px rgba(16, 29, 59, 0.08)",
              }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color: "#101D3B",
                  fontWeight: 600,
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                  pb: 1,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                }}>
                <HomeIcon sx={{mr: 1, color: "primary.main"}} />
                {formatFieldName(serviceRequest.serviceName)}
              </Typography>

              <Box sx={{display: "flex", flexDirection: "column", gap: 3, mt: 2}}>
               
                {/* Show service details */}
                {serviceRequest.serviceDetails && (
                  <Box sx={{width: "100%"}}>
                    {(() => {
                      try {
                        // Parse the JSON description if it exists
                        if (serviceRequest.serviceDetails.description) {
                          const parsedDetails = JSON.parse(serviceRequest.serviceDetails.description);
                          
                          return (
                            <>
                              {/* Water Location */}
                              {parsedDetails.waterLocation && (
                                <Box sx={{mb: 3}}>
                                  <Typography 
                                    variant="subtitle1" 
                                    sx={{
                                      fontWeight: 600, 
                                      mb: 1,
                                      display: "flex",
                                      alignItems: "center"
                                    }}>
                                    <LocationOnIcon color="primary" sx={{mr: 1, fontSize: "1.2rem"}} />
                                    Water Location
                                  </Typography>
                                  <Paper 
                                    elevation={0} 
                                    sx={{
                                      p: 2, 
                                      bgcolor: "rgba(0,0,0,0.02)", 
                                      borderRadius: "8px", 
                                      border: "1px solid rgba(0,0,0,0.05)",
                                      '&:hover': {
                                        bgcolor: "rgba(0,0,0,0.03)",
                                      }
                                    }}>
                                    <Typography variant="body2" sx={{color: "#454545"}}>
                                      {parsedDetails.waterLocation}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                              
                              {/* Additional information */}
                              {parsedDetails.serviceDescription && (
                                <Box sx={{mb: 3}}>
                                  <Typography 
                                    variant="subtitle1" 
                                    sx={{
                                      fontWeight: 600, 
                                      mb: 1,
                                      display: "flex",
                                      alignItems: "center"
                                    }}>
                                    <ArticleIcon color="primary" sx={{mr: 1, fontSize: "1.2rem"}} />
                                    Additional information
                                  </Typography>
                                  <Paper 
                                    elevation={0} 
                                    sx={{
                                      p: 2, 
                                      bgcolor: "rgba(0,0,0,0.02)", 
                                      borderRadius: "8px", 
                                      border: "1px solid rgba(0,0,0,0.05)",
                                      '&:hover': {
                                        bgcolor: "rgba(0,0,0,0.03)",
                                      }
                                    }}>
                                    <Typography variant="body2" sx={{color: "#454545", whiteSpace: "pre-wrap"}}>
                                      {parsedDetails.serviceDescription}
                                    </Typography>
                                  </Paper>
                                </Box>
                              )}
                              
                              {/* Services Details */}
                              {parsedDetails.services && Object.keys(parsedDetails.services).length > 0 && (
                                <Box sx={{mb: 3}}>
                                  <Typography 
                                    variant="subtitle1" 
                                    sx={{
                                      fontWeight: 600, 
                                      mb: 1,
                                      display: "flex",
                                      alignItems: "center"
                                    }}>
                                    <BuildIcon color="primary" sx={{mr: 1, fontSize: "1.2rem"}} />
                                    Service Specifications
                                  </Typography>
                                  <Grid container spacing={2}>
                                    {Object.entries(parsedDetails.services).map(([serviceName, serviceData]: [string, any]) => (
                                      <Grid item xs={12} key={serviceName}>
                                        <Paper 
                                          elevation={0} 
                                          sx={{
                                            p: 2, 
                                            mb: 1,
                                            bgcolor: "rgba(0,0,0,0.02)", 
                                            borderRadius: "8px", 
                                            border: "1px solid rgba(0,0,0,0.05)",
                                            '&:hover': {
                                              bgcolor: "rgba(0,0,0,0.03)",
                                            }
                                          }}
                                        >
                                          <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1, color: "primary.main"}}>
                                            {formatFieldName(serviceName)}
                                          </Typography>
                                          <Grid container spacing={2}>
                                            {Object.entries(serviceData).map(([fieldName, fieldValue]: [string, any]) => (
                                              <Grid item xs={12} sm={6} md={4} key={fieldName}>
                                                <Box 
                                                  sx={{
                                                    p: 1.5, 
                                                    borderRadius: "6px", 
                                                    bgcolor: "rgba(255,255,255,0.6)",
                                                    border: "1px solid rgba(0,0,0,0.03)",
                                                  }}
                                                >
                                                  <Typography 
                                                    variant="caption" 
                                                    sx={{
                                                      display: "block", 
                                                      color: "text.secondary", 
                                                      mb: 0.5,
                                                      fontSize: "0.7rem",
                                                      fontWeight: 500
                                                    }}
                                                  >
                                                    {formatFieldName(fieldName)}
                                                  </Typography>
                                                  <Typography variant="body2" sx={{fontWeight: 500, color: "#333"}}>
                                                    {fieldValue}
                                                  </Typography>
                                                </Box>
                                              </Grid>
                                            ))}
                                          </Grid>
                                        </Paper>
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Box>
                              )}

                              {/* Attachments */}
                              {(() => {
                                const detailsAny = serviceRequest.serviceDetails as any;
                                if (
                                  detailsAny.attachments &&
                                  Array.isArray(detailsAny.attachments) &&
                                  detailsAny.attachments.length > 0
                                ) {
                                  return (
                                    <Box sx={{mb: 1}}>
                                      <Typography 
                                        variant="subtitle1" 
                                        sx={{
                                          fontWeight: 600, 
                                          mb: 1,
                                          display: "flex",
                                          alignItems: "center"
                                        }}>
                                        <ImageIcon color="primary" sx={{mr: 1, fontSize: "1.2rem"}} />
                                        Attachments
                                      </Typography>
                                      <AttachmentGallery attachments={detailsAny.attachments} />
                                    </Box>
                                  );
                                }
                                return null;
                              })()}
                            </>
                          );
                        }
                      } catch (error) {
                        console.error("Error parsing service details JSON:", error);
                        // If JSON parsing fails, show the raw description and try to show attachments
                        return (
                          <>
                            <Typography variant="body2" paragraph sx={{mb: 2, color: "text.secondary"}}>
                              {serviceRequest.serviceDetails.description || "No description provided."}
                            </Typography>
                            
                            {/* Try to show attachments even if JSON parsing fails */}
                            {(() => {
                              const detailsAny = serviceRequest.serviceDetails as any;
                              if (
                                detailsAny.attachments &&
                                Array.isArray(detailsAny.attachments) &&
                                detailsAny.attachments.length > 0
                              ) {
                                return <AttachmentGallery attachments={detailsAny.attachments} title="Attachments" />;
                              }
                              return null;
                            })()}
                          </>
                        );
                      }
                      
                      // Fallback if there's no description or parsing fails
                      return null;
                    })()}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Paper elevation={1} sx={{p: 3, borderRadius: "12px"}}>
              <Typography variant="body1">No service details available.</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Q&A Section */}
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          mt: 4,
          mb: 2,
          fontWeight: 600,
          borderBottom: "2px solid",
          borderColor: "primary.main",
          pb: 1,
          display: "inline-block",
        }}>
        <QuestionAnswerIcon sx={{verticalAlign: "middle", mr: 1, color: "primary.main"}} />
        Questions & Answers
      </Typography>

      {serviceRequest ? (
        <QASection requestId={serviceRequest.id} />
      ) : (
        <Paper elevation={1} sx={{p: 3, borderRadius: "12px"}}>
          <Typography variant="body1">Loading questions...</Typography>
        </Paper>
      )}

      {/* Bids section */}
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          mt: 4,
          mb: 2,
          fontWeight: 600,
          borderBottom: "2px solid",
          borderColor: "primary.main",
          pb: 1,
          display: "inline-block",
        }}>
        <MonetizationOnIcon sx={{verticalAlign: "middle", mr: 1, color: "primary.main"}} />
        Vendor Bids
      </Typography>

      {bidsLoading ? (
        <Box sx={{display: "flex", justifyContent: "center", my: 4}}>
          <CircularProgress />
        </Box>
      ) : actualBids.length > 0 ? (
        <>
          <Box sx={{display: "flex", justifyContent: "flex-end", mb: 3}}>
            <AllBidsPdfExport bids={actualBids} serviceRequest={serviceRequest} />
          </Box>
          <Grid container spacing={3}>
            {actualBids.map(bid => (
              <Grid item xs={12} sm={6} key={bid.id}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "12px",
                    border: "1px solid rgba(0,0,0,0.05)",
                    borderLeft:
                      bid.status === "accepted"
                        ? "5px solid #4caf50"
                        : bid.status === "rejected"
                        ? "5px solid #f44336"
                        : "5px solid transparent",
                  }}>
                  <Box sx={{display: "flex", mb: 2, alignItems: "flex-start", justifyContent: "space-between"}}>
                    <Box sx={{display: "flex", alignItems: "center", mb: 1}}>
                      <Avatar
                        sx={{
                          mr: 2,
                          bgcolor:
                            bid.status === "accepted"
                              ? "success.main"
                              : bid.status === "rejected"
                              ? "error.main"
                              : "primary.main",
                        }}>
                        <AttachMoneyIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6">
                          ${bid.amount?.toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {bid.status === "accepted"
                            ? "Selected"
                            : bid.status === "rejected"
                            ? "Not Selected"
                            : "Pending"}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Insured Badge */}
                    <Box 
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'rgba(0, 171, 155, 0.1)',
                        color: '#00AB9B',
                        borderRadius: '16px',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        height: 'fit-content',
                        border: '1px solid rgba(0, 171, 155, 0.3)'
                      }}
                    >
                      <ShieldIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
                      INSURED
                    </Box>
                  </Box>

                  <Box sx={{flexGrow: 1, display: "flex", flexDirection: "column"}}>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                      <strong>Submitted:</strong> {formatDate(bid.createdAt)}
                    </Typography>

                    {/* Display project timing information */}
                    <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Earliest Start Date:</strong> {bid.earliestStartDate ? formatDate(bid.earliestStartDate) : 'Not specified'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Project Duration:</strong> {bid.projectDuration ? `${bid.projectDuration} days` : 'Not specified'}
                      </Typography>
                    </Box>

                    {/* Display vendor information when bid is accepted */}
                    {bid.status === "accepted" && vendorProfiles[bid.vendorId] && (
                      <Box sx={{
                        mb: 3, 
                        p: {xs: 2, sm: 3},
                        borderRadius: "12px",
                        border: "1px solid",
                        borderColor: "rgba(0, 171, 155, 0.3)",
                        bgcolor: "rgba(0, 171, 155, 0.05)"
                      }}>
                        <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1, color: "#00AB9B"}}>
                          Winning Vendor Information
                        </Typography>
                        <Typography variant="body2">
                          <strong>Company:</strong> {vendorProfiles[bid.vendorId].companyName}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Contact:</strong> {vendorProfiles[bid.vendorId].contactFullName}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Email:</strong> {vendorProfiles[bid.vendorId].contactEmail}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Phone:</strong> {vendorProfiles[bid.vendorId].contactPhoneNumber}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Address:</strong> {vendorProfiles[bid.vendorId].address}, {vendorProfiles[bid.vendorId].city}, {vendorProfiles[bid.vendorId].state} {vendorProfiles[bid.vendorId].zipCode}
                        </Typography>
                      </Box>
                    )}

                    <Typography variant="body2" sx={{mb: 1, fontWeight: 600}}>
                      Vendor Notes:
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 3,
                        bgcolor: "background.default",
                        border: "1px solid rgba(0, 0, 0, 0.08)",
                        borderRadius: "8px",
                        flexGrow: 1,
                      }}>
                      <Typography variant="body2">{bid.notes || "No additional notes provided."}</Typography>
                    </Paper>

                    {/* Display bid attachments if any */}
                    {bid.attachments && bid.attachments.length > 0 && (
                      <Box sx={{mb: 2}}>
                        <AttachmentGallery attachments={bid.attachments} title="Bid Attachments" />
                      </Box>
                    )}
                  </Box>

                  {/* Only show action buttons for pending bids on active requests */}
                  {(bid.status === "pending" || !bid.status) && serviceRequest.status === "active" && (
                    <Box sx={{display: "flex", gap: 2, mt: "auto", pt: 2}}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<DoneIcon />}
                        onClick={() => handleBidAction(bid.id, "accepted")}
                        sx={{flex: 1}}>
                        Choose as Winner
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <Paper
          elevation={1}
          sx={{
            p: 4,
            textAlign: "center",
            mb: 4,
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.05)",
          }}>
          <Typography variant="body1">
            {declinedCount > 0
              ? `No active bids received yet. ${declinedCount} vendor${
                  declinedCount > 1 ? "s" : ""
                } ignored this request.`
              : "No bids received yet."}
          </Typography>
        </Paper>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelAction}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {borderRadius: "12px"},
        }}>
        <DialogTitle id="alert-dialog-title">
          {closeWithoutWinner ? "Close Request Without Winner" : "Choose Winning Bid"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {closeWithoutWinner
              ? "Are you sure you want to close this request without selecting a winning bid? This action cannot be undone."
              : "Are you sure you want to select this bid as the winner? This will close the service request and reject all other bids automatically."}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{pb: 3, px: 3}}>
          <Button onClick={handleCancelAction} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={closeWithoutWinner ? "error" : "success"}
            variant="contained"
            autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ServiceRequestDetail;
